import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    IconButton,
    Typography,
    Box,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Loader from './Loader';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import useSafeToast from '../hooks/useSafeToast';

function ContactSupportForm({ open, handleClose }) {
    const showToast = useSafeToast();
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState(true);
    const [formData, setFormData] = useState({
        sender_email: '',
        interview_key: '',
        body: '',
        subject: '',
        is_html: false,
        files: []
    });

    useEffect(() => {
        if (open) {
            setFormData({
                sender_email: "",
                interview_key: "",
                body: "",
                subject: "",
                is_html: false,
                files: []
            });
        }
    }, [open]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files).slice(0, 5);
        setFormData({ ...formData, files });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setLoading(true);
            setLoadingText('Submitting your request...');
            const formDataToSend = new FormData();

            // Append required fields
            formDataToSend.append('sender_email', formData.sender_email);
            formDataToSend.append('body', formData.body);

            // Append optional fields if they exist
            if (formData.subject) formDataToSend.append('subject', formData.subject);
            if (formData.interview_key) formDataToSend.append('interview_key', formData.interview_key);
            formDataToSend.append('is_html', formData.is_html);

            // Append files if they exist
            formData.files.forEach(file => {
                formDataToSend.append('files', file);
            });

            const response = await fetch(`${process.env.REACT_APP_API_URL}/support/send_email`, {
                method: 'POST',
                body: formDataToSend,
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to submit request');
            }

            showToast('Your request has been submitted successfully!');
            console.log('Success:', data);
            handleClose();
        } catch (error) {
            showToast(error.message || 'There was an error submitting your request.', 'error');
            console.error('Error:', error);
        } finally {
            setLoading(false);
            setLoadingText('');
        }
    };

    return (
        <>
            <Loader loading={loading} message={loadingText} />
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        boxShadow: '0px 10px 40px rgba(0,0,0,0.2)'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        backgroundColor: '#004E89',
                        color: '#fff',
                        fontSize: '1.4rem',
                        fontWeight: 600,
                        py: 2
                    }}
                >
                    Contact Support
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            right: 12,
                            top: 12,
                            color: '#fff'
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 4 }}>
                    <form onSubmit={handleSubmit}>
                        {/* Email */}
                        <TextField
                            fullWidth
                            label="Your Email"
                            name="sender_email"
                            type="email"
                            value={formData.sender_email}
                            onChange={handleChange}
                            margin="normal"
                            required
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                                '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#004E89' }
                            }}
                        />

                        {/* Subject */}
                        <TextField
                            fullWidth
                            label="Subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            margin="normal"
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                                '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#004E89' }
                            }}
                        />

                        {/* Interview Key */}
                        <TextField
                            fullWidth
                            label="Interview Key"
                            name="interview_key"
                            value={formData.interview_key}
                            onChange={handleChange}
                            margin="normal"
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                                '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#004E89' }
                            }}
                        />

                        {/* Message */}
                        <TextField
                            fullWidth
                            label="Message / Query"
                            name="body"
                            multiline
                            rows={8}
                            value={formData.body}
                            onChange={handleChange}
                            margin="normal"
                            required
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                                '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#004E89' }
                            }}

                        />

                        {/* HTML Checkbox */}
                        {/* <FormControlLabel
                        control={
                            <Checkbox
                                name="is_html"
                                checked={formData.is_html}
                                onChange={handleChange}
                                color="primary"
                            />
                        }
                        label="Is HTML content"
                        sx={{ mt: 1 }}
                    /> */}

                        {/* File Upload */}
                        <Box
                            sx={{
                                mt: 3,
                                p: 3,
                                border: '2px dashed #004E89',
                                borderRadius: '12px',
                                textAlign: 'center',
                                backgroundColor: 'rgba(0,78,137,0.05)',
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'rgba(0,78,137,0.08)' }
                            }}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.preventDefault();
                                const droppedFiles = Array.from(e.dataTransfer.files).slice(0, 5);
                                setFormData(prev => ({ ...prev, files: droppedFiles }));
                            }}
                        >
                            <CloudUploadIcon sx={{ fontSize: 40, color: '#004E89', mb: 1 }} />
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                Drag & drop files here or click to upload
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                You can select up to 5 video/image files.
                            </Typography>
                            <input
                                type="file"
                                hidden
                                multiple
                                onChange={handleFileChange}
                                id="file-upload-input"
                            />
                            <label htmlFor="file-upload-input">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    sx={{
                                        mt: 2,
                                        borderColor: '#004E89',
                                        color: '#004E89',
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        px: 3
                                    }}
                                >
                                    Choose Files
                                </Button>
                            </label>
                        </Box>

                        {/* Show selected files */}
                        {formData.files.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    {formData.files.length} file(s) selected:
                                </Typography>
                                <ul style={{ paddingLeft: '20px', marginTop: '4px' }}>
                                    {formData.files.map((file, index) => (
                                        <li key={index}>
                                            <Typography variant="body2">
                                                {file.name} ({Math.round(file.size / 1024)} KB)
                                            </Typography>
                                        </li>
                                    ))}
                                </ul>
                            </Box>
                        )}

                        {/* Actions */}
                        <DialogActions sx={{ mt: 4, px: 0 }}>
                            <Button
                                onClick={handleClose}
                                sx={{
                                    color: '#004E89',
                                    textTransform: 'none',
                                    fontWeight: 500
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                sx={{
                                    backgroundColor: '#004E89',
                                    '&:hover': { backgroundColor: '#003766' },
                                    textTransform: 'none',
                                    px: 4,
                                    py: 1.2,
                                    borderRadius: '8px'
                                }}
                            >
                                Submit
                            </Button>
                        </DialogActions>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default ContactSupportForm;